import {
  compact,
  get,
  isEmpty,
  join,
  map,
  mapValues,
  omitBy,
  pickBy,
  reduce,
  find,
  includes,
  forEach,
  difference,
  intersection,
  isNull
} from 'lodash';
import qs from 'qs';
import moment from 'moment';
import algoliaApi from '../services/algolia-api';
import { informationNotification } from '../components/Notification';

import {
  getDatesFromRange,
  getRangeFromDates,
  formatDateRangeLabel,
  getSingleDate
} from './dates';
import { toContentTypeLabel } from './contentType';
import { SINGLE_DATE_FILTER_ID } from '../components/Filters/Filter/SingleDateFilter';

export const getTitleFilterId = pageFilters => {
  const array = [];
  if (pageFilters.title) {
    array.push('title');
    array.push('movie');
    array.push('show');
  } else {
    if (pageFilters.movie) {
      array.push('movie');
    }
    if (pageFilters.show) {
      array.push('show');
    }
  }

  return array;
};

export const getFilterValue = filter =>
  Array.isArray(filter) ? map(filter, 'value') : get(filter, 'value');

export const getActiveFilters = fields =>
  pickBy(fields, ({ value }) => !isEmpty(value));

export const updateField = (fields, id, value) => {
  const updatedField = { ...get(fields, id, {}), value };
  return { ...fields, [id]: updatedField };
};

export const updateFieldBooleanSeparator = (fields, id, booleanSeparator) => {
  const updatedField = { ...get(fields, id, {}), booleanSeparator };
  return { ...fields, [id]: updatedField };
};

export const clearFields = (fields, omittedFields = []) =>
  mapValues(fields, field => ({
    ...field,
    value: !omittedFields.includes(field.id) ? null : field.value
  }));

export const getOptionsLoader = (
  filter,
  onUpdateFilterOptions,
  loader,
  restrictedCategories = []
) => async query => {
  // workaround to prevent errors due to old api
  const opts = await loader(query, restrictedCategories);

  if (opts.options) {
    onUpdateFilterOptions(filter, opts.options);
    return opts.options;
  }

  if (Array.isArray(opts)) {
    onUpdateFilterOptions(filter, opts);
    return opts;
  }

  return [];
};

export const serializeFilter = (
  { id, value, booleanSeparator },
  transformBool
) => {
  const parsed = Array.isArray(value)
    ? join(
        map(value, 'value'),
        (booleanSeparator === true || booleanSeparator === 'OR') &&
          (id === 'show' || id === 'movie' || id === 'title') &&
          transformBool
          ? '|'
          : ','
      )
    : value.value;
  return `${id}:${parsed}`;
};

export const serializeFilters = (filters, transformBool = true) => {
  const validFilters = omitBy(filters, f => isEmpty(getFilterValue(f.value)));
  return map(validFilters, f => serializeFilter(f, transformBool));
};

export const formatLabel = (id, value) => {
  if (
    id === 'date' ||
    id === 'first_release_date' ||
    id === 'future_release_date' ||
    id === 'report_date'
  ) {
    value = formatDateRangeLabel(value);
  }
  if (id === 'contentType') {
    value = toContentTypeLabel(value);
  }
  return value;
};

const parseFilter = (currentFilters, serializedFilter) => {
  const [id, serializedValue = ''] = serializedFilter.split(/:(.+)/);
  const booleanSeparator = includes(serializedValue, '|') ? '|' : ',';
  const serializedArrayValue = compact(serializedValue.split(booleanSeparator));

  if (isEmpty(serializedArrayValue)) {
    return { ...currentFilters, [id]: null };
  }
  const booleanFilter = get(currentFilters, ['boolean'], {});

  return {
    ...currentFilters,
    [id]: map(serializedArrayValue, v => ({
      label: formatLabel(id, v),
      value: v
    })),
    boolean: { ...booleanFilter, [id]: booleanSeparator === '|' ? 'OR' : 'AND' }
  };
};

export const parseFilters = filterStrings =>
  reduce(filterStrings, parseFilter, {});

export const addSeasonsLabels = filters =>
  filters.season
    ? {
        ...filters,
        season: map(filters.season, ({ value, label }) => ({
          value,
          label: `Season ${label}`
        }))
      }
    : filters;

export const addSampleLabel = filters => {
  const updatedFilters = { ...filters };

  if (filters.panel) {
    const value = get(filters.panel, [0, 'value'], '');
    updatedFilters.panel =
      value === 'Yes'
        ? [{ value: 'Yes', label: 'Balanced' }]
        : [{ value: 'No', label: 'Total' }];
  }

  return updatedFilters;
};

export const addDateLabels = filters => {
  const updatedFilters = { ...filters };

  if (filters.date) {
    const [startDate, endDate] = getDatesFromRange(filters.date.value);
    updatedFilters.date = getRangeFromDates({ startDate, endDate });
  }

  if (filters.first_release_date) {
    const [startDate, endDate] = getDatesFromRange(
      filters.first_release_date.value
    );
    updatedFilters.first_release_date = getRangeFromDates({
      startDate,
      endDate
    });
  }

  if (filters.release_date) {
    const [startDate, endDate] = getDatesFromRange(filters.release_date.value);
    updatedFilters.release_date = getRangeFromDates({
      startDate,
      endDate
    });
  }

  if (filters.report_date) {
    const [startDate, endDate] = getDatesFromRange(filters.report_date.value);
    updatedFilters.report_date = getRangeFromDates({
      startDate,
      endDate
    });
  }

  return updatedFilters;
};

export const addAsyncLabels = async filters => {
  const updatedFilters = { ...filters };

  if (filters.network) {
    const networkIds = map(filters.network, 'value');
    const { options: networks } = await algoliaApi.getNetworksById(networkIds);
    updatedFilters.network = networks;
  }

  if (filters.platform) {
    const platformIds = map(filters.platform, 'value');
    const { options: platforms } = await algoliaApi.getPlatformsById(
      platformIds
    );
    updatedFilters.platform = platforms;
  }

  if (filters.studio) {
    const studioIds = map(filters.studio, 'value');
    const { options: studios } = await algoliaApi.getStudiosById(studioIds);
    updatedFilters.studio = studios;
  }

  if (filters.show) {
    const showIds = map(filters.show, 'value');
    const { options: shows } = await algoliaApi.getShowsById(showIds);
    updatedFilters.show = shows;
  }

  if (filters.title) {
    const titleIds = map(filters.title, 'value');
    const [shows, movies] = await algoliaApi.getTitlesById(titleIds);
    if (shows && shows.options) {
      updatedFilters.title = [...shows.options];
    }

    if (movies && movies.options) {
      updatedFilters.title = [...updatedFilters.title, ...movies.options];
    }
  }

  if (filters.movie) {
    const movieIds = map(filters.movie, 'value');
    const movies = await algoliaApi.getMoviesById(movieIds);
    const movieOptions = movies.options;
    updatedFilters.movie = movieOptions;
  }

  return updatedFilters;
};

export const parseSegmentUrl = (filterString, fields) => {
  const parsedFilters = parseFilters(
    get(qs.parse(filterString), 'filters', [])
  );

  const withSeasons = addSeasonsLabels(parsedFilters);
  const withMultiValues = mapValues(withSeasons, (f, id) =>
    get(fields, `${id}.isMulti`, false) ? f : get(f, '0', null)
  );
  return addDateLabels(withMultiValues);
};

export const extractFilterValues = fields => {
  const filterValues = mapValues(fields, 'value');
  const keys = Object.keys(filterValues);
  const array = [];
  keys.forEach(k => {
    if (!filterValues[k]) {
      return null;
    }

    if (Array.isArray(filterValues[k])) {
      const values = filterValues[k].map(a => a.value).join(',');

      if (values) {
        const string = `${k}:${values}`;
        array.push(string);
      }
    } else if (typeof filterValues[k] === 'object') {
      const values = filterValues[k].value;
      if (values) {
        const string = `${k}:${values}`;
        array.push(string);
      }
    }
  });

  return array;
};

export const extractMetricFilter = (filterValue, types, operators) => {
  const values = filterValue.split(' ');
  const isFilterWithOneOperand = values.length === 3;
  const isFilterWithTwoOperands = values.length === 4;

  if (!isFilterWithOneOperand && !isFilterWithTwoOperands) {
    return null;
  }

  let type;
  let operator;
  let param1;
  let param2;

  if (isFilterWithOneOperand) {
    [type, operator, param1] = values;
  } else if (isFilterWithTwoOperands) {
    [type, param1, operator, param2] = values;
  }

  const formatType = t => find(types, { value: t });
  const formatOperator = o => find(operators, { value: o });

  return {
    metricType: formatType(type),
    operand: param1,
    operator: formatOperator(operator),
    secondOperand: isFilterWithOneOperand ? '' : param2,
    isOperatorBetween: operator === 'to'
  };
};

export const getInitialFilterFieldsState = (fields, allowedDataCategories) => {
  const fieldsState = {};
  map(fields, (obj, field) => {
    fieldsState[field] = { ...obj, value: null };
  });

  if (allowedDataCategories) {
    allowedDataCategories.forEach(restrictionString => {
      const [restrictedField, restriction] = restrictionString.split(':');
      if (restriction !== '*') {
        const values = restriction
          .split(',')
          .map(v => ({ label: v, value: v }));

        if (fieldsState && fieldsState[restrictedField]) {
          fieldsState[restrictedField].value =
            values.length > 1 ? values : values[0];
        }
      }
    });
  }

  return fieldsState;
};

export const formatAllowedFilter = (allowedDataCategories, key) => {
  let values;
  allowedDataCategories.forEach(restrictionString => {
    const [restrictedField, restriction] = restrictionString.split(':');

    if (key === 'date' && restrictedField === 'date' && restriction !== '*') {
      const startDate = moment().subtract(restriction, 'months');
      const endDate = moment();
      const range = getRangeFromDates({ startDate, endDate });
      values = [range];
    } else if (restrictedField === key && restriction !== '*') {
      values = restriction.split(',').map(v => {
        const label = formatLabel(key, v);
        return { label, value: v };
      });
    }
  });

  return values;
};

export const displaySampleBalanceInformation = (invalidCountry = false) =>
  invalidCountry
    ? informationNotification({
        title: 'Sample Balance',
        text: 'A balanced sample is not available in the markets selected'
      })
    : informationNotification({
        title: 'Sample Balance',
        text: 'A country filter is needed in order to sample balance'
      });

export const hasOnlyValidCountries = countries => !isEmpty(countries);

export const hasSampleBalance = (fields, countries) => {
  const hasSampleBalanceFilterBalanced =
    get(fields, ['panel', 'value', 'value'], null) === 'Yes';

  return hasSampleBalanceFilterBalanced && !hasOnlyValidCountries(countries);
};

export const handleCountryChange = (countries, fields, changedFields) => {
  if (!countries || !countries.length) {
    const newFields = updateField(fields, 'panel', null);
    const newChangedFields = {
      ...changedFields,
      panel: true
    };
    return { newFields, newChangedFields };
  }

  return { newFields: fields, newChangedFields: changedFields };
};

export const defaultCountriesValue = allowedCountries => {
  const defaults = [
    { label: 'United States', value: 'United States', base: 'United States' },
    { label: 'France', value: 'France', base: 'France' },
    { label: 'Italy', value: 'Italy', base: 'Italy' },
    {
      label: 'United Kingdom',
      value: 'United Kingdom',
      base: 'United Kingdom'
    },
    { label: 'Spain', value: 'Spain', base: 'Spain' },
    { label: 'Germany', value: 'Germany', base: 'Germany' }
  ];

  if (!allowedCountries || !allowedCountries.length) {
    return defaults;
  }

  return defaults.filter(c => allowedCountries.includes(c.value));
};

export const getAllowedUserCountries = allowedDataCategories => {
  const countriesString = get(allowedDataCategories, '0', 'country:*');
  if (!countriesString || countriesString === 'country:*') {
    return [];
  }

  return countriesString.split(':')[1].split(',');
};

export const handleSampleBalanceChange = (fields, changedFields) => {
  const hasSampleBalanceFilterBalanced =
    get(fields, ['panel', 'value', 'value'], null) === 'Yes';
  const countries = get(fields, ['country', 'value'], []);

  if (hasSampleBalanceFilterBalanced && !countries.length) {
    const newFields = updateField(fields, 'panel', null);
    const newChangedFields = {
      ...changedFields,
      panel: false
    };

    return { newFields, newChangedFields };
  }

  return { newFields: fields, newChangedFields: changedFields };
};

export const handleSegmentChange = (segment, fields, changedFields) => {
  let filtersToAdd = get(segment, [0, 'filters'], '[]');
  filtersToAdd =
    typeof filtersToAdd === 'string' ? JSON.parse(filtersToAdd) : filtersToAdd;

  const validTitleFilters = getTitleFilterId(fields);
  const newFields = fields;

  if (validTitleFilters.includes('title')) {
    const titleValue = [];
    const showValues = get(newFields, ['show', 'value'], []);
    const movieValues = get(newFields, ['movie', 'value'], []);

    if (showValues) {
      titleValue.push(...showValues);
      if (newFields.show) {
        newFields.show.value = null;
      }
    }

    if (movieValues) {
      titleValue.push(...movieValues);
      if (newFields.movie) {
        newFields.movie.value = null;
      }
    }

    newFields.title.value = titleValue;
  }
  const newChangedFields = changedFields;
  let hasChanges = false;

  forEach(filtersToAdd, k => {
    if (newFields[k.id]) {
      hasChanges = true;
      newFields[k.id].value = k.value;
    } else if (k.id === 'movie' && validTitleFilters.includes('title')) {
      hasChanges = true;
      newFields.title.value = [...newFields.title.value, ...k.value];
    } else if (k.id === 'show' && validTitleFilters.includes('title')) {
      hasChanges = true;
      newFields.title.value = [...newFields.title.value, ...k.value];
    }
  });

  return hasChanges
    ? { newFields, newChangedFields }
    : { newFields: fields, newChangedFields: hasChanges };
};

export const getChangedFilters = (nextFilters = [], prevFilters = []) => {
  const changedFilters =
    nextFilters.length > prevFilters.length
      ? difference(nextFilters, prevFilters)
      : difference(prevFilters, nextFilters);
  return changedFilters;
};

export const isExpectedFilters = (filters = [], expectedFilters = []) => {
  return !isEmpty(intersection(filters, expectedFilters));
};

export const getFilterNames = (filterValues = []) => {
  return map(filterValues, filter => {
    return filter.split(':')[0];
  });
};

export const getFieldDefaultSingleDateFilter = (fields, dates) => {
  return reduce(
    fields,
    (result, value, key) => {
      if (key === SINGLE_DATE_FILTER_ID) {
        const singleDateValue = get(value, 'value');
        if (!singleDateValue && dates.length > 0) {
          const defaultSingleDate = getSingleDate(dates[dates.length - 1]);
          result[key] = Object.assign({}, result[key], {
            value: defaultSingleDate
          });
        }
      }
      return result;
    },
    {}
  );
};

export const removeNulls = obj => {
  // eslint-disable-next-line no-restricted-syntax
  for (const k of Object.keys(obj)) {
    if (isNull(obj[k])) {
      delete obj[k];
    } else if (typeof obj[k] === 'object') {
      removeNulls(obj[k]);
    }
  }
  return obj;
};
